import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { FieldState, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";
import { AiProposalParse } from "./bindAiProposal";

/**
 * One entity field plus how to coerce its proposal string into that field's type.
 * `T` is erased in mixed arrays (`AiFieldBinding[]`); use `aiFieldBinding` so each entry keeps field and parse aligned.
 */
export type AiFieldBinding<T = any> = {
  field: FieldState<T>;
  parse: AiProposalParse<T>;
  proposal?: ObjectState<AiFieldProposalFormValue>;
};

export function aiFieldBinding<T>(
  field: FieldState<T>,
  parse: AiProposalParse<NoInfer<T>>,
  proposal?: ObjectState<AiFieldProposalFormValue>,
): AiFieldBinding<T> {
  return { field, parse, proposal };
}

/**
 * Accept All / Ignore All for the bindings you pass. Does not call a mutation.
 *
 * Accepted: parse `fieldValue`, `field.set` it, mark Accepted.
 * Rejected: mark Rejected only.
 *
 * Every pending proposal it is given ends up resolved. A value we can't parse - an empty
 * `fieldValue`, or a room number outside its level's
 * range — can't be applied, so it is recorded as Rejected: we couldn't honor it. Leaving it Pending
 * instead means the review banner never clears and the user has no way to dismiss it but editing that
 * one field.
 */
export function reconcilePendingAiProposals(
  bindings: AiFieldBinding[],
  decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected,
): void {
  for (const { field, parse, proposal } of bindings) {
    // No proposal on this field, or already Accepted/Rejected.
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) continue;
    if (decision === AiFieldProposalStatus.Accepted) {
      // Coerce GraphQL's string `fieldValue` into the entity field's type.
      const proposedValue = parse(proposal.fieldValue.value);
      // Unparseable (empty or junk) is not "clear the field". Reject it without changing the value.
      if (proposedValue === undefined) {
        proposal.status.set(AiFieldProposalStatus.Rejected);
        continue;
      }
      field.set(proposedValue);
    }
    proposal.status.set(decision);
  }
}

/** Resolves pending proposals without applying values. Reconcile field bindings first when using both functions. */
export function resolvePendingAiProposals(
  proposals: readonly ObjectState<AiFieldProposalFormValue>[],
  decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected,
): void {
  for (const proposal of proposals) {
    if (proposal.status.value !== AiFieldProposalStatus.Pending) continue;
    proposal.status.set(decision);
  }
}
